import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../store/useStore';
import { updatePassword, signOut } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { deleteUserAndData, updateUserLanguage } from '../utils/firebase';
import { type SupportedCurrency, CURRENCY_SYMBOLS } from '../services/CurrencyService';
import { useCurrencyConverter } from '../hooks/useCurrencyConverter';

interface UserProfileProps {
  onUsageClick?: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onUsageClick }) => {
  // Subscribe to specific store values
  const user = useStore(state => state.user);
  const setUser = useStore(state => state.setUser);
  const toggleConfigPanel = useStore(state => state.toggleConfigPanel);
  const language = useStore(state => state.language);
  const toggleDarkMode = useStore(state => state.toggleDarkMode);
  const darkMode = useStore(state => state.darkMode);
  const topUpValue = useStore(state => state.topUpValue);
  const usagePreferences = useStore(state => state.usagePreferences);
  const userCurrency = (usagePreferences.currency || 'USD') as SupportedCurrency;

  // Convert topUpValue to user's preferred currency
  const { convertedAmount, symbol } = useCurrencyConverter({
    amount: topUpValue,
    fromCurrency: 'USD',
    toCurrency: userCurrency
  });

  const [showMenu, setShowMenu] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Force re-render when topUpValue changes
  useEffect(() => {
    console.log('TopUpValue changed:', topUpValue);
  }, [topUpValue]);

  useEffect(() => {
    if (showMenu) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          profileButtonRef.current &&
          !profileButtonRef.current.contains(event.target as Node) &&
          dropdownMenuRef.current &&
          !dropdownMenuRef.current.contains(event.target as Node)
        ) {
          setShowMenu(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, newPassword);
        setSuccess('Password updated successfully');
        setShowPasswordForm(false);
        setNewPassword('');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update password');
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      setUser(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to log out');
    }
  };

  const handleCurrencyChange = async (newCurrency: SupportedCurrency) => {
    if (user) {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'usagePreferences.currency': newCurrency,
        lastUpdated: new Date().toISOString()
      });
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center space-x-4 relative">
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            {/* Currency Selector */}
            <div className="relative">
              {/* Desktop select */}
              <select
                value={userCurrency}
                onChange={async (e) => {
                  const newCurrency = e.target.value as SupportedCurrency;
                  await handleCurrencyChange(newCurrency);
                  window.location.reload();
                }}
                className="hidden md:block px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200
                          text-sm border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2
                          focus:ring-blue-400 dark:focus:ring-blue-600"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="CNY">CNY</option>
                <option value="GBP">GBP</option>
              </select>
              
              {/* Mobile select */}
              <select
                value={userCurrency}
                onChange={async (e) => {
                  const newCurrency = e.target.value as SupportedCurrency;
                  await handleCurrencyChange(newCurrency);
                  window.location.reload();
                }}
                className="md:hidden px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200
                          text-sm border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2
                          focus:ring-blue-400 dark:focus:ring-blue-600"
              >
                <option value="USD">{CURRENCY_SYMBOLS.USD}</option>
                <option value="EUR">{CURRENCY_SYMBOLS.EUR}</option>
                <option value="CNY">{CURRENCY_SYMBOLS.CNY}</option>
                <option value="GBP">{CURRENCY_SYMBOLS.GBP}</option>
              </select>
            </div>

            {/* Balance Button */}
            <div className="relative group">
              <button
                onClick={() => onUsageClick && onUsageClick()}
                className={`flex items-center px-4 py-2 transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]
                           ${Number(convertedAmount) < 1 ? 'bg-[#B82132] dark:bg-[#B82132] text-white' : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'}
                           rounded-full shadow-sm hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]
                           focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2
                           dark:focus:ring-white dark:focus:ring-offset-gray-900 border border-gray-900/30 dark:border-white/30
                           relative overflow-hidden`}
                aria-label={language === 'es' ? 'Ver uso y costos' : 'View usage and costs'}
              >
                {/* Animated ripple effect */}
                <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100
                                transition-opacity duration-300 pointer-events-none" />
                
                {/* Value display with currency symbol and number transition */}
                <span key={convertedAmount} className={`text-base font-semibold tracking-tight transition-all ${Number(convertedAmount) < 1 ? 'text-white' : 'text-white/90 dark:text-gray-900'}`}>
                  {symbol}{Number(convertedAmount).toFixed(2)}
                </span>
              </button>
              
              {/* Ambient glow effect */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-400/20 to-blue-600/20
                              blur-[8px] rounded-xl opacity-0 group-hover:opacity-100 group-focus:opacity-100
                              transition-opacity duration-500 pointer-events-none" />
            </div>
          </div>
        </div>
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {darkMode ? (
            <svg className="w-5 h-5 text-gray-800 dark:text-gray-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-800 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button
          ref={profileButtonRef}
          onClick={() => setShowMenu(!showMenu)}
          className="flex items-center space-x-2 p-2 md:px-4 md:py-2 aspect-square md:aspect-auto rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <div className="md:hidden">
            <svg className="w-6 h-6 text-gray-800 dark:text-gray-200" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </div>
          <div className="hidden md:block">
            {user?.photoURL ? (
              <img src={user.photoURL} alt={user.displayName || 'User'} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
          <span className="hidden md:inline text-sm font-medium dark:text-gray-200">{user?.displayName || user?.email}</span>
        </button>
      </div>

      {showMenu && (
        <div ref={dropdownMenuRef} className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
          <button
            onClick={async () => {
              setShowMenu(false);
              const newLanguage = language === 'en' ? 'es' : 'en';
              if (user) {
                await updateUserLanguage(user.uid, newLanguage);
              }
              setUser({ ...user, language: newLanguage } as any);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              {language === 'es' ? 'Cambiar a EN' : 'Switch to ES'}
            </div>
          </button>
          {onUsageClick && (
            <button
              onClick={() => {
                setShowMenu(false);
                onUsageClick();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {language === 'es' ? 'Uso y Costes' : 'Usage & Costs'}
              </div>
            </button>
          )}
          <button
            onClick={() => {
              setShowMenu(false);
              toggleConfigPanel();
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {language === 'es' ? 'Configuración API' : 'API Settings'}
            </div>
          </button>
          <button
            onClick={() => {
              setShowMenu(false);
              setShowPasswordForm(true);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              {language === 'es' ? 'Cambiar contraseña' : 'Change Password'}
            </div>
          </button>
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </div>
          </button>
          <button
            onClick={async () => {
              if (window.confirm('Are you sure you want to delete your account and all data? This action cannot be undone.')) {
                try {
                  if (user) {
                    await deleteUserAndData(user.uid);
                    setShowMenu(false);
                    setShowPasswordForm(false);
                    setError('');
                    setSuccess('');
                    setUser(null);
                  }
                } catch (error) {
                  setError(error instanceof Error ? error.message : 'Failed to delete account');
                }
              }
            }}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Account
            </div>
          </button>
        </div>
      )}

      {showPasswordForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-lg font-bold mb-4">{language === 'es' ? 'Cambiar contraseña' : 'Change Password'}</h2>
            {error && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">
                {success}
              </div>
            )}
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {language === 'es' ? 'Nueva contraseña' : 'New Password'}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setNewPassword('');
                    setError('');
                    setSuccess('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {language === 'es' ? 'Actualizar Contraseña' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;